import type { DbClient } from "@tanstack/db";
import { apiPost, apiPut } from "@softmax/observatory-client/client";
import type {
  PostCommentPublic,
  VotePublic,
} from "@softmax/observatory-client/types";
import { postDetailCollections } from "./post.collections";

export async function createPostComment({
  db,
  postId,
  parentId,
  body,
  idempotencyKey,
  elevatedPrivileges,
}: {
  db: DbClient;
  postId: string;
  parentId: string | null;
  body: string;
  idempotencyKey: string;
  elevatedPrivileges: boolean;
}) {
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const comment = await apiPost<PostCommentPublic>(
    `/v2/posts/${encodeURIComponent(postId)}/comments`,
    { body, parent_id: parentId, idempotency_key: idempotencyKey },
    { elevatedPrivileges, signal },
  ).catch((error: Error) => {
    db.requireDependency<(error: Error) => void>("reportAuthorizationError")(
      error,
    );
    throw error;
  });
  signal.throwIfAborted();
  const descriptors = postDetailCollections({ postId, elevatedPrivileges });
  await db.collection(descriptors.comments).utils.writeUpsert(comment);
  signal.throwIfAborted();
  void db.collection(descriptors.post).utils.refetch();
  return comment;
}

export async function voteForComment({
  db,
  postId,
  commentId,
  value,
  elevatedPrivileges,
}: {
  db: DbClient;
  postId: string;
  commentId: string;
  value: -1 | 0 | 1;
  elevatedPrivileges: boolean;
}) {
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const vote = await apiPut<VotePublic>(
    `/v2/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/vote`,
    { value },
    { elevatedPrivileges, signal },
  ).catch((error: Error) => {
    db.requireDependency<(error: Error) => void>("reportAuthorizationError")(
      error,
    );
    throw error;
  });
  signal.throwIfAborted();
  const source = db.collection(
    postDetailCollections({ postId, elevatedPrivileges }).comments,
  );
  await source.utils.writeUpdate({ id: commentId, score: vote.score });
  return vote;
}
